// The free Prop Challenge Survival Guide: the sign-up form, its landing page
// (/guide and /es/guide), the box at the end of each article, and the corner
// slide-in on the homepage and articles.
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from './api';
import { consentChoice, trackLead } from './analytics';
import { GA_MEASUREMENT_ID } from './config';
import { PublicShell, usePageLanguage, useTitle } from './PublicPage';
import { useI18n } from './i18n';

const SUBSCRIBED_KEY = 'mm-guide-subscribed';
const SLIDE_KEY = 'mm-guide-slide';
// After the slide-in has been shown once, it stays away this long.
const SLIDE_PAUSE_DAYS = 21;

function readKey(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeKey(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Private mode: the slide-in may show again next time.
  }
}

// Email box + button. `source` says where the sign-up came from (for the admin page).
export function GuideForm({ source, compact = false }) {
  const { t, lang } = useI18n();
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault();
    setError('');
    setStatus('sending');
    try {
      await api('/guide/subscribe', { method: 'POST', body: { email, company, lang, source } });
      writeKey(SUBSCRIBED_KEY, '1');
      trackLead(source);
      setStatus('done');
    } catch (err) {
      setError(err.message);
      setStatus('idle');
    }
  }

  if (status === 'done') {
    return (
      <div className="guide-done" role="status">
        <strong>{t('Check your inbox.')}</strong>
        <span>{t('The guide is on its way to {email}. If you don’t see it in a few minutes, look in spam or promotions.', { email })}</span>
      </div>
    );
  }

  return (
    <form className={`guide-form${compact ? ' compact' : ''}`} onSubmit={submit}>
      <div className="guide-form-row">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('you@example.com')}
          aria-label={t('Email')} autoComplete="email" />
        <button type="submit" disabled={status === 'sending'}>{status === 'sending' ? t('Sending…') : t('Send me the guide')}</button>
      </div>
      {/* People never see this field; bots that fill it in are ignored. */}
      <input type="text" name="company" value={company} onChange={(e) => setCompany(e.target.value)} className="guide-honeypot"
        tabIndex={-1} autoComplete="off" aria-hidden="true" />
      {error && <p className="error">{error}</p>}
      <p className="guide-consent">
        {t('We’ll email you the guide and a few trading tips. Unsubscribe anytime.')}{' '}
        <Link to="/privacy">{t('Privacy Policy')}</Link>
      </p>
    </form>
  );
}

function GuideCover({ lang }) {
  return <img src={`/guide/cover-${lang === 'es' ? 'es' : 'en'}.jpg`} alt="" className="guide-cover" width="612" height="792" />;
}

export function GuidePage({ user, forceLang }) {
  const lang = usePageLanguage(forceLang, '/guide');
  const { t } = useI18n();
  useTitle(`${t('Prop Challenge Survival Guide')} | Market Matrix`);
  const points = [
    t('Why your $50K account is really a $2,000 account, and how trailing drawdown moves'),
    t('Risk-per-trade tables for 50K, 100K and 150K accounts, with MNQ, MES and NQ'),
    t('The 5 mistakes that blow most evaluations, and a checklist for before every session'),
  ];

  return (
    <PublicShell user={user} className="guide-page">
      <section className="guide-hero">
        <div className="guide-hero-text">
          <p className="eyebrow">{t('Free guide · PDF')}</p>
          <h1>{t('Prop Challenge Survival Guide')}</h1>
          <p className="subtitle">{t('The math and habits that keep a funded-account evaluation alive. Free, in your inbox in a minute.')}</p>
          <ul className="guide-points">
            {points.map((p) => <li key={p}>{p}</li>)}
          </ul>
          <GuideForm source={lang === 'es' ? 'Guide page (es)' : 'Guide page'} />
        </div>
        <GuideCover lang={lang} />
      </section>
    </PublicShell>
  );
}

// The box at the end of every article.
export function GuideBox({ source }) {
  const { t, lang } = useI18n();
  return (
    <section className="guide-box" aria-labelledby="guide-box-title">
      <GuideCover lang={lang} />
      <div>
        <p className="eyebrow">{t('Free guide · PDF')}</p>
        <h2 id="guide-box-title">{t('Prop Challenge Survival Guide')}</h2>
        <p>{t('Drawdown math, risk-per-trade tables for 50K to 150K accounts and a pre-session checklist, in one free PDF.')}</p>
        <GuideForm source={source} compact />
      </div>
    </section>
  );
}

// Pages where the slide-in may appear: the homepages and articles.
const SLIDE_PAGES = /^\/(es\/?)?$|^\/(es\/)?blog\/[a-z0-9-]+\/?$/;

function slideAllowed() {
  if (readKey(SUBSCRIBED_KEY)) return false;
  const shown = Number(readKey(SLIDE_KEY));
  if (shown && Date.now() - shown < SLIDE_PAUSE_DAYS * 86400000) return false;
  // Never on top of the cookie question.
  if (GA_MEASUREMENT_ID && !consentChoice()) return false;
  return true;
}

// A small corner box after the visitor reads half of an article, or near the end of the
// homepage (never while its pricing is on screen). Shown once, then not
// again for a few weeks; never for members or people who already have the guide.
export function GuideSlideIn({ user }) {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [boxInView, setBoxInView] = useState(false);
  const eligible = !user && SLIDE_PAGES.test(pathname);

  useEffect(() => {
    if (!eligible || open) return undefined;
    function onScroll() {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      const home = !pathname.includes('/blog/');
      if (scrollable <= 0 || window.scrollY / scrollable < (home ? 0.85 : 0.5) || !slideAllowed()) return;
      writeKey(SLIDE_KEY, String(Date.now()));
      setOpen(true);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [eligible, open, pathname]);

  // Steps aside while the same offer is on screen at the end of an article, and while the
  // homepage's pricing is.
  useEffect(() => {
    const boxes = open ? [...document.querySelectorAll('.guide-box, #pricing')] : [];
    if (!boxes.length || !window.IntersectionObserver) return undefined;
    const seen = new Map();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((e) => seen.set(e.target, e.isIntersecting));
      setBoxInView([...seen.values()].some(Boolean));
    });
    boxes.forEach((b) => observer.observe(b));
    return () => observer.disconnect();
  }, [open, pathname]);

  if (!open || !eligible || boxInView) return null;
  const source = pathname.includes('/blog/') ? `Slide-in: ${pathname.replace(/^\/(es\/)?blog\//, '').replace(/\/$/, '')}` : 'Slide-in: homepage';

  return (
    <aside className="guide-slide" aria-label={t('Free guide')}>
      <button type="button" className="guide-slide-close" onClick={() => setOpen(false)} aria-label={t('Close')}>&times;</button>
      <p className="eyebrow">{t('Free guide · PDF')}</p>
      <strong className="guide-slide-title">{t('Prop Challenge Survival Guide')}</strong>
      <p>{t('The drawdown math and sizing tables that keep an evaluation alive.')}</p>
      <GuideForm source={source} compact />
    </aside>
  );
}
